<template>
  <div
    v-if="visible"
    class="quick-edit-collect-modal modal fade show"
    style="display: block; background-color: rgba(0, 0, 0, 0.5)"
    tabindex="-1"
    @click.self="closeModal"
  >
    <div class="modal-dialog modal-md">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">
            <i class="fas fa-pencil-alt me-2 text-info" aria-hidden="true"></i>
            {{ $t("financialSummary.stages.quickEditCollectTitle") }}
          </h5>
          <button type="button" class="btn-close" :aria-label="$t('close')" @click="closeModal"></button>
        </div>
        <div class="modal-body">
          <p class="small text-muted mb-3">
            {{ $t("financialSummary.stages.quickEditCollectHint") }}
          </p>
          <div v-if="serverError" class="alert alert-danger mb-3">
            {{ serverError }}
          </div>
          <ContentSkeleton v-if="loading" variant="form" :rows="4" padded />
          <template v-else-if="collectDoc">
            <div class="mb-2 small text-body text-break">
              <span class="text-muted">{{ $t("financialSummary.name") }}:</span>
              <span class="fw-semibold ms-1">{{ collectDoc.name || "—" }}</span>
            </div>
            <div class="mb-3">
              <label class="form-label small">{{ $t("financialSummary.stages.quickEditCollectLineAmountLabel") }}</label>
              <input
                v-model="form.lineAmountStr"
                type="text"
                inputmode="decimal"
                class="form-control form-control-sm"
                :disabled="saving || deleting"
              />
            </div>
            <div class="row g-2 mb-3">
              <div class="col-6">
                <label class="form-label small mb-1" for="quick-edit-collect-rounding">{{
                  $t("financialSummary.quickAddCollectAppliedtoRounding")
                }}</label>
                <input
                  id="quick-edit-collect-rounding"
                  v-model="form.lineRoundingStr"
                  type="text"
                  inputmode="decimal"
                  class="form-control form-control-sm"
                  :disabled="saving || deleting"
                />
              </div>
              <div class="col-6">
                <label class="form-label small mb-1" for="quick-edit-collect-commission">{{
                  $t("financialSummary.quickAddCollectAppliedtoCommission")
                }}</label>
                <input
                  id="quick-edit-collect-commission"
                  v-model="form.lineCommissionStr"
                  type="text"
                  inputmode="decimal"
                  class="form-control form-control-sm"
                  :disabled="saving || deleting"
                />
              </div>
            </div>
            <div class="mb-3">
              <label class="form-label small">{{ $t("financialSummary.stages.quickEditCollectDateLabel") }}</label>
              <input
                v-model="form.collection_date"
                type="date"
                class="form-control form-control-sm"
                :disabled="saving || deleting"
              />
            </div>
            <div class="mb-3 form-check">
              <input
                id="quick-edit-collect-received"
                v-model="form.received"
                type="checkbox"
                class="form-check-input"
                :disabled="saving || deleting"
              />
              <label class="form-check-label small" for="quick-edit-collect-received">{{
                $t("financialSummary.received")
              }}</label>
            </div>
            <div v-if="bankOptions.length" class="mb-3">
              <label class="form-label small">{{ $t("Bank account") }}</label>
              <select
                v-model="form.bankaccountId"
                class="form-select form-select-sm"
                :disabled="saving || deleting"
              >
                <option value="">{{ $t("financialSummary.stages.quickEditCollectBankPlaceholder") }}</option>
                <option v-for="b in bankOptions" :key="String(b._id)" :value="String(b._id)">
                  {{ (b.label || b.name || b._id) + "" }}
                </option>
              </select>
            </div>
            <div class="mb-0">
              <label class="form-label small">{{ $t("financialSummary.stages.quickMarkPaymentPaidReceiptLabel") }}</label>
              <div class="input-group input-group-sm">
                <input
                  ref="fileInput"
                  type="file"
                  class="form-control"
                  accept="image/*,.pdf,.doc,.docx"
                  :disabled="uploadingFile || saving || deleting"
                  @change="handleFileUpload"
                />
                <button
                  v-if="form.file"
                  type="button"
                  class="btn btn-outline-danger"
                  :disabled="uploadingFile || saving || deleting"
                  @click="removeFile"
                >
                  <i class="fas fa-times" aria-hidden="true"></i>
                </button>
              </div>
              <div v-if="form.file" class="mt-1 small text-success">
                <i class="fas fa-file me-1" aria-hidden="true"></i>{{ displayFileName(form.file) }}
              </div>
            </div>
          </template>
        </div>
        <div
          v-if="!loading && collectDoc"
          class="modal-body border-top pt-3 pb-2"
        >
          <label class="form-label small mb-1" for="quick-edit-collect-comment">{{
            $t("Comment")
          }}</label>
          <textarea
            id="quick-edit-collect-comment"
            v-model="form.comment"
            class="form-control form-control-sm"
            rows="2"
            :disabled="saving || deleting"
          ></textarea>
        </div>
        <div class="modal-footer d-flex flex-wrap gap-2 justify-content-between">
          <button
            type="button"
            class="btn btn-outline-danger"
            :disabled="saving || deleting || loading || !canDeleteCollect"
            @click="removeCollect"
          >
            <span v-if="deleting" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
            {{ $t("financialSummary.fobNotaComercial.deleteButton") }}
          </button>
          <div class="d-flex gap-2 ms-auto">
            <button type="button" class="btn btn-outline-secondary" :disabled="saving || deleting" @click="closeModal">
              {{ $t("financialSummary.fobNotaComercial.cancel") }}
            </button>
            <button type="button" class="btn btn-primary" :disabled="saving || deleting || loading" @click="submit">
              <span v-if="saving" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
              {{ saving ? $t("financialSummary.fobNotaComercial.saving") : $t("financialSummary.fobNotaComercial.save") }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { wencesApi } from "@/api/wencesApi";
import Swal from "sweetalert2";
import { toYyyyMmDdSync } from "@/utils/dateFormatter";
import { saveConfirmingContactOutsideOperation } from "@/utils/dynamicModelValidationSwal";
import ContentSkeleton from "@/components/common/ContentSkeleton.vue";

function unwrapDynamicDoc(res) {
  const d = res && res.data;
  if (d && d.data != null && typeof d.data === "object") return d.data;
  if (d && typeof d.data === "object" && !Array.isArray(d.data)) return d.data;
  return d && typeof d === "object" ? d : null;
}

function refId(val) {
  if (val == null || val === "") return "";
  if (typeof val === "object" && val._id != null) return String(val._id);
  return String(val).trim();
}

function parseAmount(raw) {
  if (raw === "" || raw == null) return NaN;
  const n = parseFloat(String(raw).replace(",", ".").trim());
  return Number.isFinite(n) ? n : NaN;
}

/**
 * Rounding / bank commission de una línea appliedto.
 * Vacío o ilegible se guarda como 0, igual que en el modal de creación
 * (QuickAddCollectModal): 0 y "sin cargar" significan lo mismo acá.
 */
function parseLineExtra(raw) {
  const n = parseAmount(raw);
  return Number.isFinite(n) ? n : 0;
}

/** El 0 se muestra vacío para no llenar el formulario de ceros sin información. */
function lineExtraToStr(value) {
  if (value === null || value === undefined || value === "") return "";
  const n = Number(value);
  if (!Number.isFinite(n) || n === 0) return "";
  return String(n);
}

export default {
  name: "QuickEditCollectModal",
  components: { ContentSkeleton },
  props: {
    lists: { type: Array, default: () => [] },
  },
  data() {
    return {
      visible: false,
      loading: false,
      saving: false,
      deleting: false,
      uploadingFile: false,
      serverError: "",
      collectDoc: null,
      collectId: "",
      appliedToLineId: "",
      form: {
        lineAmountStr: "",
        lineRoundingStr: "",
        lineCommissionStr: "",
        collection_date: "",
        received: false,
        bankaccountId: "",
        comment: "",
        file: null,
      },
      initialFile: null,
    };
  },
  computed: {
    bankOptions() {
      const L = this.lists || [];
      for (const doc of L) {
        if (!doc || !Array.isArray(doc.values) || !doc.values.length) continue;
        const t = String(doc.type || doc.listType || doc.name || doc.key || "").toLowerCase();
        if (t.includes("bank") || t.includes("cuenta") || t.includes("account")) {
          return doc.values.filter((v) => v && v._id);
        }
      }
      return [];
    },
    /** Cobro cargado en el modal: habilita borrado aunque `received` sea true. */
    canDeleteCollect() {
      return !!(this.collectDoc && this.collectId);
    },
  },
  methods: {
    displayFileName(file) {
      if (typeof file === "string") return file.split("/").pop() || file;
      return file?.name || "—";
    },
    closeModal() {
      this.visible = false;
      this.collectDoc = null;
      this.collectId = "";
      this.appliedToLineId = "";
      this.serverError = "";
      this.form = {
        lineAmountStr: "",
        lineRoundingStr: "",
        lineCommissionStr: "",
        collection_date: "",
        received: false,
        bankaccountId: "",
        comment: "",
        file: null,
      };
      this.initialFile = null;
      if (this.$refs.fileInput) this.$refs.fileInput.value = "";
    },
    async open(payload) {
      if (!payload || !payload.collectId) return;
      this.serverError = "";
      this.collectId = String(payload.collectId);
      this.appliedToLineId = payload.appliedToLineId != null ? String(payload.appliedToLineId) : "";
      const lineAmt = Number(payload.lineAmount);
      this.visible = true;
      this.loading = true;
      this.collectDoc = null;
      try {
        const res = await wencesApi.get(`/api/gotrading/dynamic/collect/${encodeURIComponent(this.collectId)}`);
        const doc = unwrapDynamicDoc(res);
        if (!doc || !doc._id) {
          await Swal.fire({
            icon: "error",
            title: this.$tn("financialSummary.fobNotaComercial.errorTitle"),
            text: this.$tn("financialSummary.stages.quickEditCollectLoadFailed"),
          });
          this.closeModal();
          return;
        }
        this.collectDoc = doc;
        const amtStr =
          Number.isFinite(lineAmt) && lineAmt > 0
            ? String(lineAmt)
            : (() => {
                const lines = this.collectAppliedToLines(doc);
                const hit = lines.find((l) => String(l._id) === this.appliedToLineId);
                const m = hit ? Number(hit.amount) : NaN;
                return Number.isFinite(m) ? String(m) : "";
              })();
        this.form.lineAmountStr = amtStr;
        // Rounding y bank commission son de la línea, no del cobro: se leen de la fila que
        // se está editando y solo se escriben en esa (ver buildAppliedToForPut).
        const targetLine = this.collectAppliedToLines(doc).find(
          (l) => String(l._id) === this.appliedToLineId
        );
        this.form.lineRoundingStr = lineExtraToStr(targetLine && targetLine.rounding);
        this.form.lineCommissionStr = lineExtraToStr(targetLine && targetLine.bank_commission);
        this.form.collection_date = toYyyyMmDdSync(doc.date || doc.collection_date || doc.collectionDate || "") || "";
        const r = doc.received;
        this.form.received = !!(r === true || r === "true" || r === 1 || r === "1");
        this.form.bankaccountId = refId(doc.bankaccount);
        this.form.comment = doc.comment != null ? String(doc.comment) : "";
        this.form.file = doc.file || null;
        this.initialFile = this.form.file;
      } catch (e) {
        await Swal.fire({
          icon: "error",
          title: this.$tn("financialSummary.fobNotaComercial.errorTitle"),
          text: e?.response?.data?.message || e?.response?.data?.error || e?.message || this.$tn("financialSummary.stages.quickEditCollectLoadFailed"),
        });
        this.closeModal();
      } finally {
        this.loading = false;
      }
    },
    collectAppliedToLines(doc) {
      const raw = doc && (doc.appliedto || doc.applied_to);
      return Array.isArray(raw) ? raw : [];
    },
    buildAppliedToForPut(doc, newLineAmount) {
      const lines = this.collectAppliedToLines(doc);
      const targetId = this.appliedToLineId;
      const hasTarget = !!(targetId && lines.some((l) => String(l._id) === targetId));
      return lines.map((l) => {
        const id = l._id != null ? String(l._id) : "";
        const isTarget = hasTarget && id === targetId;
        const amt = isTarget ? newLineAmount : Number(l.amount) || 0;
        return {
          ...(l._id ? { _id: l._id } : {}),
          proform: refId(l.proform) || null,
          shipping: refId(l.shipping) || null,
          amount: amt,
          // Solo la línea editada lleva estos dos campos. Las demás viajan sin ellos, y el
          // back hace $set de lo que llega, así que sus valores quedan como estaban.
          ...(isTarget
            ? {
                rounding: parseLineExtra(this.form.lineRoundingStr),
                bank_commission: parseLineExtra(this.form.lineCommissionStr),
              }
            : {}),
        };
      });
    },
    async handleFileUpload(event) {
      const file = event.target.files && event.target.files[0];
      if (!file) return;
      const maxSize = 10 * 1024 * 1024;
      if (file.size > maxSize) {
        await Swal.fire({ icon: "error", title: this.$tn("error"), text: this.$tn("financialSummary.stages.quickMarkPaymentPaidFileTooBig") });
        return;
      }
      try {
        this.uploadingFile = true;
        const formData = new FormData();
        formData.append("image", file);
        const response = await wencesApi.post("/api/upload/upload", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        if (response.data && response.data.fileKey) {
          this.form.file = response.data.fileKey;
        } else {
          throw new Error((response.data && response.data.message) || "upload failed");
        }
      } catch (e) {
        await Swal.fire({ icon: "error", title: this.$tn("error"), text: this.$tn("financialSummary.stages.quickMarkPaymentPaidFileError") });
      } finally {
        this.uploadingFile = false;
      }
    },
    removeFile() {
      this.form.file = null;
      if (this.$refs.fileInput) this.$refs.fileInput.value = "";
    },
    async removeCollect() {
      if (!this.collectId || !this.canDeleteCollect) return;
      const result = await Swal.fire({
        icon: "warning",
        title: this.$tn("financialSummary.stages.quickEditCollectDeleteConfirmTitle"),
        text: this.$tn("financialSummary.stages.quickEditCollectDeleteConfirmText"),
        showCancelButton: true,
        confirmButtonText: this.$tn("YesDeleteIt"),
        cancelButtonText: this.$tn("cancel"),
        confirmButtonColor: "#dc3545",
      });
      if (!result.isConfirmed) return;
      this.deleting = true;
      this.serverError = "";
      try {
        await wencesApi.delete(
          `/api/gotrading/dynamic/collect/${encodeURIComponent(this.collectId)}/permanent`
        );
        await Swal.fire({
          icon: "success",
          title: this.$tn("financialSummary.stages.quickEditCollectDeleteSuccessTitle"),
          text: this.$tn("financialSummary.stages.quickEditCollectDeleteSuccessText"),
          timer: 1800,
          showConfirmButton: false,
        });
        this.closeModal();
        this.$emit("saved");
      } catch (error) {
        const msg =
          error?.response?.data?.message || error?.response?.data?.error || error?.message || this.$t("error");
        this.serverError = msg;
        await Swal.fire({ icon: "error", title: this.$tn("error"), text: msg });
      } finally {
        this.deleting = false;
      }
    },
    async submit() {
      if (!this.collectDoc || !this.collectId) return;
      const newAmt = parseAmount(this.form.lineAmountStr);
      if (!Number.isFinite(newAmt) || newAmt <= 0) {
        await Swal.fire({
          icon: "warning",
          title: this.$tn("financialSummary.fobNotaComercial.errorTitle"),
          text: this.$tn("financialSummary.fobNotaComercial.needAmount"),
        });
        return;
      }
      const appliedto = this.buildAppliedToForPut(this.collectDoc, newAmt);
      const collectionDateVal = this.form.collection_date || null;
      const payload = {
        name: this.collectDoc.name != null ? String(this.collectDoc.name) : "",
        date: collectionDateVal,
        collection_date: collectionDateVal,
        received: !!this.form.received,
        comment: this.form.comment != null ? String(this.form.comment) : "",
      };
      if (appliedto.length) {
        const sumApplied = appliedto.reduce((s, x) => s + (Number(x.amount) || 0), 0);
        payload.amount = sumApplied > 0.005 ? sumApplied : newAmt;
        payload.appliedto = appliedto;
      } else {
        payload.amount = newAmt;
      }
      if (this.form.bankaccountId) {
        payload.bankaccount = this.form.bankaccountId;
      }
      if (this.form.file !== this.initialFile) {
        payload.file = this.form.file;
      }
      this.saving = true;
      this.serverError = "";
      try {
        const saved = await saveConfirmingContactOutsideOperation(
          (key, params) => this.$t(key, params),
          (forceSave) =>
            wencesApi.put(
              `/api/gotrading/dynamic/collect/${encodeURIComponent(this.collectId)}`,
              forceSave ? { ...payload, forceSave: true } : payload
            )
        );
        if (!saved) return;
        await Swal.fire({
          icon: "success",
          title: this.$tn("financialSummary.stages.quickEditCollectSaveSuccessTitle"),
          text: this.$tn("financialSummary.stages.quickEditCollectSaveSuccessText"),
          timer: 1800,
          showConfirmButton: false,
        });
        this.closeModal();
        this.$emit("saved");
      } catch (error) {
        const msg =
          error?.response?.data?.message || error?.response?.data?.error || error?.message || this.$t("error");
        this.serverError = msg;
        await Swal.fire({ icon: "error", title: this.$tn("error"), text: msg });
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>
